/**
 * Global axios auth interceptor.
 *
 * Attaches `Authorization: Bearer <token>` to every outbound request IF a
 * token is present in localStorage. Also handles 401/403 by clearing the
 * token and bouncing back to login.
 *
 * Import this file ONCE from App.js (or index.js) and every axios.get/post
 * call in the app becomes authenticated automatically.
 */
import axios from 'axios';
import { clearSession } from './sessionTimeout';

const TOKEN_KEYS = ['access_token', 'auth_token', 'token'];

const getToken = () => {
  for (const k of TOKEN_KEYS) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return null;
};

// ─── Request interceptor: attach Bearer token + strip SA session from URLs ───
// Phase 4B §19 — the SA session token must never travel in a URL. If the
// outbound URL contains the stored SA session id, it is replaced with the
// literal placeholder `sa-session` and the real token moves to the
// `X-SA-Session` header (backend middleware restores it before routing).
const getSASession = () => {
  try {
    return localStorage.getItem('super_admin_session') || localStorage.getItem('sa_session_id');
  } catch { return null; }
};

axios.interceptors.request.use((config) => {
  try {
    const token = getToken();
    if (token && !config.headers?.Authorization) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
    }
    const sa = getSASession();
    if (sa && config.url && config.url.includes(sa)) {
      config.url = config.url.split(sa).join('sa-session');
      config.headers = { ...(config.headers || {}), 'X-SA-Session': sa };
    }
  } catch { /* ignore */ }
  return config;
});

// ─── Response interceptor: 401 → log out ───
// Public / auth routes where a 401 from a background fetch must NOT yank
// the user off the page. (e.g. /signup loading trade categories while the
// user is unauthenticated — we expect the 401 and the user is in the
// middle of creating an account.)
const PUBLIC_PATH_PREFIXES = [
  '/login', '/analytics-login', '/signup', '/',
  '/forgot-password', '/reset-password', '/verify-email',
  '/terms-and-policy', '/build-experience', '/setup',
  '/founders-checklist', '/pro/', '/customer-portal/', '/remodify', '/find',
  '/super-admin/login', '/super-admin/',
];

const isOnPublicRoute = () => {
  if (typeof window === 'undefined' || !window.location) return false;
  const p = window.location.pathname;
  // Exact root '/' handled via startsWith('/') — restrict to exact match
  if (p === '/') return true;
  return PUBLIC_PATH_PREFIXES.some((prefix) =>
    prefix === '/' ? false : p === prefix || p.startsWith(prefix + '/') || p.startsWith(prefix),
  );
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      // Never redirect on auth endpoints themselves (login/signup/etc.)
      const isAuthEndpoint = /\/api\/auth\/(email-login|login|signup|verify|refresh|logout)/.test(url);
      if ((status === 401) && !isAuthEndpoint && !isOnPublicRoute()) {
        // Full clear (founder session ruling 2026-09-27): leaving user_data /
        // analytics_user behind created a half-logged-in "glitch" state.
        clearSession();
        if (typeof window !== 'undefined' && window.location) {
          // Tools-only users get routed back to the tools-signup loop —
          // NEVER to the Provider /login (different identity context).
          const toolsOnly = (() => {
            try { return localStorage.getItem('tools_only_mode') === '1'; } catch { return false; }
          })();
          const p = window.location.pathname || '';
          const onToolPath =
            p === '/remodforms' || p.startsWith('/remodforms/') ||
            p === '/remodpay'   || p.startsWith('/remodpay/') ||
            p === '/remodinventory' || p.startsWith('/remodinventory/');
          window.location.href = (toolsOnly || onToolPath) ? '/tools/signup' : '/login?expired=1';
        }
      }
    } catch { /* ignore */ }
    return Promise.reject(error);
  },
);

export default axios;
